import React, { useMemo, useState } from 'react';
import { AlertTriangle, Youtube, Tv } from 'lucide-react';
import './ServerOverview.css';

interface LapEntry {
  lapTime: number;
  sector1: number;
  sector2: number;
  sector3: number;
  hasCollision: boolean;
  collisionDetails?: {
    withWhat: 'car' | 'wall' | 'object';
    hasDamage: boolean;
    otherDriver?: string;
  };
  status?: string;
  pitThisLap?: boolean;
  positionChange?: number;
}

interface TelemetryEntry {
  position?: number;
  driverName?: string;
  carName?: string;
  carNumber?: string;
  bestLapMs?: number;
  lastLapMs?: number;
  lapsCompleted?: number;
  pitStops?: number;
  status?: string;
  disconnected?: boolean;
  hasCollision?: boolean;
  collisionDetails?: {
    withWhat: 'car' | 'wall' | 'object';
    hasDamage: boolean;
    otherDriver?: string;
  };
  positionChange?: number;
  laps?: LapEntry[];
}

interface SessionInfo {
  code: 'P' | 'Q' | 'W' | 'R';
  label: string;
  hasStarted: boolean;
  hasEnded: boolean;
}

interface ProgressionDriver {
  driverName: string;
  driverId: string;
  positions: Array<{ lap: number; position: number }>;
}

export interface LiveDataResponse {
  sessionCode?: 'P' | 'Q' | 'W' | 'R' | null;
  sessionLabel?: string;
  progressPercent?: number;
  timeLeftSeconds?: number;
  sessionEnded?: boolean;
  allSessions?: SessionInfo[];
  liveData: TelemetryEntry[];
  sessionSnapshots?: Partial<Record<'P' | 'Q' | 'W' | 'R', TelemetryEntry[]>>;
  raceProgression?: ProgressionDriver[];
}

interface SessionTab {
  id: string;
  code: 'P' | 'Q' | 'W' | 'R';
  label: string;
  value: string;
}

interface LiveDataModalProps {
  title: string;
  subtitle: string;
  sessions: SessionTab[];
  liveData: LiveDataResponse | null;
  isLoading: boolean;
  initialSessionCode?: 'P' | 'Q' | 'W' | 'R' | null;

  isEnded?: boolean;
  endedAt?: number;
  displayTimezone?: string;

  canBroadcast?: boolean;
  broadcastPlatform?: 'youtube' | 'twitch' | '';
  broadcastChannel?: string;
  broadcastChannelId?: string;

  onClose: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatLapTime = (ms?: number): string => {
  if (typeof ms !== 'number' || ms <= 0 || Number.isNaN(ms)) return '—';
  const totalSeconds = ms / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = (totalSeconds % 60).toFixed(3);
  return `${minutes}:${seconds.padStart(6, '0')}`;
};

const buildEmbedUrl = (platform: string, channel: string, channelId?: string): string | null => {
  if (platform === 'youtube') {
    const id = channelId?.trim();
    if (!id) return null;
    return `https://www.youtube.com/embed/live_stream?channel=${encodeURIComponent(id)}&autoplay=1`;
  }
  if (platform === 'twitch') {
    if (!channel) return null;
    const name = channel.startsWith('http')
      ? (channel.match(/twitch\.tv\/([^/?&#]+)/)?.[1] ?? channel)
      : channel;
    return `https://player.twitch.tv/?channel=${encodeURIComponent(name)}&parent=${window.location.hostname}&autoplay=true`;
  }
  return null;
};

type TabCode = 'P' | 'Q' | 'W' | 'R' | 'progression';

// ─── Race Progression Chart ───────────────────────────────────────────────────

const CHART_COLORS = [
  '#22d3ee', '#34d399', '#fb923c', '#a78bfa',
  '#f472b6', '#facc15', '#60a5fa', '#4ade80',
  '#f87171', '#c084fc',
];

const LiveProgressionChart: React.FC<{ drivers: ProgressionDriver[] }> = ({ drivers }) => {
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const toggle = (id: string) =>
    setHidden((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const lapCount = drivers.reduce((m, d) => Math.max(m, ...d.positions.map(p => p.lap)), 0);
  const driverCount = drivers.length;

  if (!driverCount || lapCount < 1) {
    return <p className="modal-empty">No race lap data yet.</p>;
  }

  const W = 680, H = 300, PAD = { top: 20, right: 16, bottom: 36, left: 32 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;
  const xScale = (lap: number) => (lap / Math.max(lapCount, 1)) * chartW;
  const yScale = (pos: number) => ((pos - 1) / Math.max(driverCount - 1, 1)) * chartH;

  return (
    <div className="progression-chart-container">
      <svg viewBox={`0 0 ${W} ${H}`} className="progression-svg" aria-label="Race position progression">
        {Array.from({ length: driverCount }, (_, i) => (
          <g key={i}>
            <line
              x1={PAD.left} x2={PAD.left + chartW}
              y1={PAD.top + yScale(i + 1)} y2={PAD.top + yScale(i + 1)}
              stroke="rgba(148,163,184,0.12)" strokeWidth={1}
            />
            <text x={PAD.left - 6} y={PAD.top + yScale(i + 1) + 4} textAnchor="end" className="chart-axis-label">
              {i + 1}
            </text>
          </g>
        ))}

        {Array.from({ length: Math.min(lapCount, 20) }, (_, i) => {
          const lap = Math.round(1 + (i / Math.max(Math.min(lapCount - 1, 19), 1)) * (lapCount - 1));
          return (
            <text key={lap} x={PAD.left + xScale(lap)} y={H - PAD.bottom + 16} textAnchor="middle" className="chart-axis-label">
              {lap}
            </text>
          );
        })}
        <text x={PAD.left} y={H - PAD.bottom + 16} textAnchor="middle" className="chart-axis-label chart-axis-label-grid">
          Grid
        </text>

        {drivers.map((driver, idx) => {
          if (hidden.has(driver.driverId)) return null;
          const color = CHART_COLORS[idx % CHART_COLORS.length];
          const pts = driver.positions
            .map(p => `${PAD.left + xScale(p.lap)},${PAD.top + yScale(p.position)}`)
            .join(' ');
          if (!pts) return null;
          return (
            <polyline
              key={driver.driverId}
              points={pts}
              fill="none"
              stroke={color}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity={0.9}
            />
          );
        })}

        <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + chartH} stroke="rgba(148,163,184,0.3)" strokeWidth={1} />
        <line x1={PAD.left} y1={PAD.top + chartH} x2={PAD.left + chartW} y2={PAD.top + chartH} stroke="rgba(148,163,184,0.3)" strokeWidth={1} />
      </svg>

      <div className="progression-legend">
        {drivers.map((driver, idx) => {
          const color = CHART_COLORS[idx % CHART_COLORS.length];
          const isHidden = hidden.has(driver.driverId);
          return (
            <button
              key={driver.driverId}
              type="button"
              className={`progression-legend-item ${isHidden ? 'hidden' : ''}`}
              onClick={() => toggle(driver.driverId)}
            >
              <span className="legend-dot" style={{ background: isHidden ? 'transparent' : color, border: `2px solid ${color}` }} />
              {driver.driverName}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const LiveDataModal: React.FC<LiveDataModalProps> = ({
  title,
  subtitle,
  sessions,
  liveData,
  isLoading,
  initialSessionCode,
  isEnded = false,
  endedAt,
  displayTimezone,
  canBroadcast = false,
  broadcastPlatform = '',
  broadcastChannel = '',
  broadcastChannelId = '',
  onClose,
}) => {
  const initialTab: TabCode = initialSessionCode ?? (sessions[0]?.code ?? 'P');
  const [activeTab, setActiveTab] = useState<TabCode>(initialTab);
  const [expandedDriverId, setExpandedDriverId] = useState<string | null>(null);
  const [showBroadcast, setShowBroadcast] = useState(false);

  const embedUrl = showBroadcast && canBroadcast
    ? buildEmbedUrl(broadcastPlatform, broadcastChannel, broadcastChannelId)
    : null;

  const isRace = activeTab === 'R';
  const hasRaceTab = sessions.some((s) => s.code === 'R');

  const toggleRow = (driverId: string) =>
    setExpandedDriverId((prev) => (prev === driverId ? null : driverId));

  // Select which driver data to display for the active tab.
  // Current session → live liveData; past session → snapshot; progression → empty (chart handles it).
  const activeTabData = useMemo<TelemetryEntry[]>(() => {
    if (!liveData || activeTab === 'progression') return [];
    const code = activeTab as 'P' | 'Q' | 'W' | 'R';
    if (code === liveData.sessionCode) return liveData.liveData;
    return liveData.sessionSnapshots?.[code] ?? [];
  }, [liveData, activeTab]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className={`modal-card live-data-card${embedUrl ? ' live-data-card--broadcast' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header">
          <div>
            <h4>{title}</h4>
            <p>{liveData?.sessionLabel || subtitle}</p>
          </div>
          <div className="modal-header-actions">
            {canBroadcast && (
              <button
                type="button"
                className={`button-ghost broadcast-btn${showBroadcast ? ' broadcast-btn--active' : ''}`}
                onClick={() => setShowBroadcast((v) => !v)}
              >
                {broadcastPlatform === 'youtube' ? <Youtube size={14} /> : <Tv size={14} />}
                {showBroadcast ? 'Hide stream' : 'Broadcast'}
              </button>
            )}
            <button type="button" className="icon-button" onClick={onClose}>×</button>
          </div>
        </div>

        {/* Broadcast + telemetry layout */}
        <div className={embedUrl ? 'live-modal-broadcast-layout' : undefined}>
          {embedUrl && (
            <div className="live-modal-stream">
              <div className="theater-video">
                <iframe
                  src={embedUrl}
                  title="Live broadcast"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          )}

          <div className="live-modal-telemetry">
            {isLoading && <p className="modal-empty">Loading telemetry…</p>}

            {!isLoading && isEnded && endedAt != null && (
              <div className="session-ended-banner">
                <h3>ENDED</h3>
                <p>
                  Session concluded{' '}
                  {new Date(endedAt).toLocaleString(undefined, { timeZone: displayTimezone })}.
                  {' '}Data retained for 24 hours.
                </p>
              </div>
            )}

            {!isLoading && !isEnded && liveData?.sessionEnded && (
              <div className="session-ended-banner">
                <h3>ENDED</h3>
                <p>This race weekend has concluded. Data will remain visible until the server is reused.</p>
              </div>
            )}

            {!isLoading && liveData && (
              <>
                {/* Session tabs */}
                <div className="telemetry-tabs">
                  {sessions.map((session) => {
                    const sessionData = liveData.allSessions?.find((s) => s.code === session.code);
                    const hasStarted = sessionData?.hasStarted ?? false;
                    return (
                      <button
                        key={session.code}
                        type="button"
                        className={`telemetry-tab ${activeTab === session.code ? 'active' : ''} ${!hasStarted ? 'disabled' : ''}`}
                        onClick={() => {
                          if (hasStarted) {
                            setActiveTab(session.code);
                            setExpandedDriverId(null);
                          }
                        }}
                        disabled={!hasStarted}
                      >
                        {session.label}
                      </button>
                    );
                  })}
                  {hasRaceTab && (
                    <button
                      type="button"
                      className={`telemetry-tab ${activeTab === 'progression' ? 'active' : ''} ${!(liveData.allSessions?.find(s => s.code === 'R')?.hasStarted) ? 'disabled' : ''}`}
                      disabled={!(liveData.allSessions?.find(s => s.code === 'R')?.hasStarted)}
                      onClick={() => {
                        if (liveData.allSessions?.find(s => s.code === 'R')?.hasStarted) {
                          setActiveTab('progression');
                          setExpandedDriverId(null);
                        }
                      }}
                    >
                      Race Progression
                    </button>
                  )}
                </div>

                {/* Content */}
                {activeTab === 'progression' ? (
                  <div className="race-progression-content">
                    {liveData.raceProgression && liveData.raceProgression.length > 0
                      ? <LiveProgressionChart drivers={liveData.raceProgression} />
                      : <p className="modal-empty">No race lap data yet.</p>
                    }
                  </div>
                ) : (
                  <div className="telemetry-table-container">
                    {!activeTabData.length ? (
                      <p className="modal-empty">No telemetry data available for this session.</p>
                    ) : (
                      <div className="telemetry-table">
                        {/* Header */}
                        <div className={`telemetry-header ${isRace ? 'race-layout' : ''}`}>
                          <span className="col-pos">Pos</span>
                          <span className="col-driver">Driver</span>
                          <span className="col-car-num">Car #</span>
                          <span className="col-car">Car</span>
                          <span className="col-best">Best Lap</span>
                          <span className="col-last">Last Lap</span>
                          {isRace && (
                            <>
                              <span className="col-laps">Laps</span>
                              <span className="col-pitstops">Pit Stops</span>
                            </>
                          )}
                          <span className="col-status">Status</span>
                          <span className="col-events">Events</span>
                        </div>

                        {/* Rows */}
                        {activeTabData.map((entry, index) => {
                          const rowId = entry.driverName || `driver-${index}`;
                          return (
                          <React.Fragment key={rowId}>
                            <div
                              className={`telemetry-row ${isRace ? 'race-layout' : ''} ${entry.disconnected ? 'driver-left' : ''} ${expandedDriverId === rowId ? 'expanded' : ''}`}
                              onClick={() => toggleRow(rowId)}
                            >
                              <span className="col-pos">{entry.position ?? index + 1}</span>
                              <span className="col-driver">{entry.driverName || '—'}</span>
                              <span className="col-car-num">{entry.carNumber || '—'}</span>
                              <span className="col-car">{entry.carName || '—'}</span>
                              <span className="col-best">{formatLapTime(entry.bestLapMs)}</span>
                              <span className="col-last">{formatLapTime(entry.lastLapMs)}</span>
                              {isRace && (
                                <>
                                  <span className="col-laps">{entry.lapsCompleted ?? '—'}</span>
                                  <span className="col-pitstops">{entry.pitStops ?? 0}</span>
                                </>
                              )}
                              <span className="col-status">{entry.status || 'On Track'}</span>
                              <span className="col-events">
                                {entry.hasCollision && (
                                  <span
                                    className="event-icon collision-icon"
                                    title={
                                      entry.collisionDetails
                                        ? `Collision with ${entry.collisionDetails.withWhat}${entry.collisionDetails.hasDamage ? ' (damaged)' : ''}${entry.collisionDetails.otherDriver ? ` — ${entry.collisionDetails.otherDriver}` : ''}`
                                        : 'Collision'
                                    }
                                  >
                                    <AlertTriangle size={14} />
                                  </span>
                                )}
                                {isRace && entry.positionChange !== undefined && entry.positionChange !== 0 && (
                                  <span className={`position-change ${entry.positionChange > 0 ? 'gained' : 'lost'}`}>
                                    {entry.positionChange > 0 ? '↑' : '↓'}
                                    {Math.abs(entry.positionChange) > 1 && Math.abs(entry.positionChange)}
                                  </span>
                                )}
                              </span>
                            </div>

                            {expandedDriverId === rowId && (entry.laps?.length ?? 0) > 0 && (
                              <div className="lap-details-panel">
                                <div className="lap-details-header">
                                  <span>Lap</span>
                                  <span>Time</span>
                                  <span>S1</span>
                                  <span>S2</span>
                                  <span>S3</span>
                                  <span>Events</span>
                                </div>
                                {entry.laps!.map((lap, lapIndex) => (
                                  <div key={lapIndex} className="lap-detail-row">
                                    <span>{lapIndex + 1}</span>
                                    <span>{formatLapTime(lap.lapTime)}</span>
                                    <span>{formatLapTime(lap.sector1 || undefined)}</span>
                                    <span>{formatLapTime(lap.sector2 || undefined)}</span>
                                    <span>{formatLapTime(lap.sector3 || undefined)}</span>
                                    <span className="lap-events">
                                      {lap.pitThisLap && (
                                        <span className="lap-event lap-event-pit" title="Pit stop">PIT</span>
                                      )}
                                      {lap.hasCollision && (
                                        <span
                                          className="event-icon collision-icon"
                                          title={
                                            lap.collisionDetails
                                              ? `Collision with ${lap.collisionDetails.withWhat}${lap.collisionDetails.hasDamage ? ' (damaged)' : ''}${lap.collisionDetails.otherDriver ? ` — ${lap.collisionDetails.otherDriver}` : ''}`
                                              : 'Collision'
                                          }
                                        >
                                          <AlertTriangle size={12} />
                                        </span>
                                      )}
                                      {isRace && lap.positionChange !== undefined && lap.positionChange !== 0 && (
                                        <span className={`position-change ${lap.positionChange > 0 ? 'gained' : 'lost'}`}>
                                          {lap.positionChange > 0 ? '↑' : '↓'}
                                          {Math.abs(lap.positionChange) > 1 && Math.abs(lap.positionChange)}
                                        </span>
                                      )}
                                      {!lap.pitThisLap && !lap.hasCollision && (!isRace || !lap.positionChange) && '—'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </React.Fragment>
                        );
                      })}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveDataModal;
